import { Pill, Row } from "@gadgetinc/widgets";
import { DownLeftArrowIcon } from "@gadgetinc/widgets/src/icons/DownLeftArrowIcon";
import { ForwardArrowIcon } from "@gadgetinc/widgets/src/icons/ForwardArrowIcon";
import { useStyletron } from "baseui";
import { omit } from "lodash";
import { observer } from "mobx-react-lite";
import React from "react";
import type { EnvironmentSlug, LegacyEnvironmentName } from "state-trees/src/Environment";
import { DevelopmentEnvironmentName, ProductionEnvironmentName } from "state-trees/src/Environment";
import type { StyleObject } from "styletron-react";
import { useProductionEnvironment } from "../../workspace/EnvironmentContext";

export type EnvironmentNameTagProps = JSX.IntrinsicElements["span"] & {
  environment: LegacyEnvironmentName | EnvironmentSlug;
  $style?: StyleObject;
};

export const DeployAwareEnvironmentNameTag = observer((props: EnvironmentNameTagProps & { pendingDeploy?: boolean }) => {
  const [_, $theme] = useStyletron();

  const prodRoot = useProductionEnvironment();
  const prodSettings = prodRoot.settings.getOrSuspend();

  const isPublished = prodSettings.isEnvironmentActive;
  const isProduction = props.environment == ProductionEnvironmentName;

  const pendingDeploy = isProduction && (!isPublished || props.pendingDeploy);

  return pendingDeploy ? (
    <EnvironmentNameTag {...omit(props, "pendingDeploy")}>
      <Row $gap={$theme.sizing.scale200}>
        <ForwardArrowIcon />
        pending deploy
      </Row>
    </EnvironmentNameTag>
  ) : (
    <EnvironmentNameTag {...props} />
  );
});

/**
 * Span which auto colors the text based on the environment name
 * No background
 **/
export const EnvironmentNameTag = (props: EnvironmentNameTagProps) => {
  // eslint-disable-next-line react/no-unknown-property
  const isProduction = props.environment.toLowerCase() == "production";
  const [css, $theme] = useStyletron();
  return (
    <Pill
      $color={isProduction ? "production" : "teal"}
      title={props.environment.toLowerCase()}
      $style={{ maxWidth: "100%", paddingLeft: isProduction ? $theme.sizing.scale100 : "3px" }}
    >
      <span
        className={css({
          color: isProduction ? $theme.colors.contentProduction : $theme.colors.contentDevelopment,
          fontWeight: 500,
          height: "20px",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          ...props.$style,
        })}
        data-widget="environment-name-tag"
        {...omit(props, ["$style", "environment"])}
        title={props.environment.toLowerCase()}
      >
        {props.children ?? (
          <EnvironmentNameLabel
            showArrowIcon={!isProduction && typeof window !== "undefined" && window.GadgetEditConfig?.multiEnvironmentEnabled} // We use this in docs so window is not always available during SSR
            envName={props.environment}
          />
        )}
      </span>
    </Pill>
  );
};

export const EnvironmentNameLabel = (props: { showArrowIcon?: boolean; envName: string }) => {
  const [css, $theme] = useStyletron();
  return (
    <>
      {props.showArrowIcon && (
        <DownLeftArrowIcon
          $style={{ marginRight: $theme.sizing.scale200, maxHeight: $theme.sizing.scale600, verticalAlign: "text-bottom" }}
        />
      )}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{props.envName.toLowerCase()}</span>
    </>
  );
};

/**
 * Span which auto colors the text based on the environment name
 * Pill shaped with a background
 **/
export const EnvironmentNamePill = (props: EnvironmentNameTagProps) => {
  const [_css, $theme] = useStyletron();
  return (
    <EnvironmentNameTag
      {...props}
      $style={{
        borderRadius: $theme.borders.radius200,
        padding: "0px 4px",
        ...props.$style,
      }}
    />
  );
};

export const EnvironmentNameTags = {
  Development: (props: { $style?: StyleObject }) => (
    <EnvironmentNameTag {...props} environment={DevelopmentEnvironmentName}>
      Development
    </EnvironmentNameTag>
  ),
  development: (props: { $style?: StyleObject }) => (
    <EnvironmentNameTag {...props} environment={DevelopmentEnvironmentName}>
      development
    </EnvironmentNameTag>
  ),
  Production: (props: { $style?: StyleObject }) => (
    <EnvironmentNameTag {...props} environment={ProductionEnvironmentName}>
      Production
    </EnvironmentNameTag>
  ),
  production: (props: { $style?: StyleObject }) => (
    <EnvironmentNameTag {...props} environment={ProductionEnvironmentName}>
      production
    </EnvironmentNameTag>
  ),
};

(EnvironmentNameTags.Development as any).textContent = "Development";
(EnvironmentNameTags.development as any).textContent = "development";
(EnvironmentNameTags.Production as any).textContent = "Production";
(EnvironmentNameTags.production as any).textContent = "production";
